import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import {
  createQuizApi,
  duplicateQuizApi,
  getQuizApi,
  QUESTION_TYPE_LABEL,
  updateQuizApi,
  type QuizQuestionType,
  type SaveQuizPayload,
} from "../quizApi";
import { QUIZ_CAMPAIGN_NEW_PAGE, QUIZ_EDIT_PAGE, QUIZ_HUB_PAGE } from "../routes";
import "./Quiz.css";

type DraftOption = { key: string; label: string; isCorrect: boolean };
type DraftQuestion = {
  key: string;
  prompt: string;
  type: QuizQuestionType;
  weight: number;
  explanation: string;
  options: DraftOption[];
};

let seq = 0;
const newKey = () => `k${++seq}`;

const TRUE_FALSE_OPTIONS = (): DraftOption[] => [
  { key: newKey(), label: "Verdadeiro", isCorrect: true },
  { key: newKey(), label: "Falso", isCorrect: false },
];

const emptyQuestion = (): DraftQuestion => ({
  key: newKey(),
  prompt: "",
  type: "SINGLE_CHOICE",
  weight: 1,
  explanation: "",
  options: [
    { key: newKey(), label: "", isCorrect: true },
    { key: newKey(), label: "", isCorrect: false },
  ],
});

const QuizEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack(QUIZ_HUB_PAGE());

  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState<DraftQuestion[]>(() => [emptyQuestion()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const quiz = await getQuizApi(id);
        setTitle(quiz.title);
        setDescription(quiz.description ?? "");
        setLocked(quiz.locked);
        setQuestions(
          quiz.questions.map((q) => ({
            key: newKey(),
            prompt: q.prompt,
            type: q.type,
            weight: q.weight,
            explanation: q.explanation ?? "",
            options: q.options.map((o) => ({ key: newKey(), label: o.label, isCorrect: o.isCorrect })),
          })),
        );
      } catch (e: any) {
        toast.error(e?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const updateQuestion = (key: string, patch: Partial<DraftQuestion>) =>
    setQuestions((list) => list.map((q) => (q.key === key ? { ...q, ...patch } : q)));

  const changeType = (q: DraftQuestion, type: QuizQuestionType) => {
    if (type === "TRUE_FALSE") return updateQuestion(q.key, { type, options: TRUE_FALSE_OPTIONS() });
    // Sair da múltipla deixa só a primeira correta.
    let seen = false;
    const options =
      type === "SINGLE_CHOICE"
        ? q.options.map((o) => {
            const isCorrect = o.isCorrect && !seen;
            if (isCorrect) seen = true;
            return { ...o, isCorrect };
          })
        : q.options;
    updateQuestion(q.key, { type, options });
  };

  const toggleCorrect = (q: DraftQuestion, optionKey: string) => {
    const options =
      q.type === "MULTI_CHOICE"
        ? q.options.map((o) => (o.key === optionKey ? { ...o, isCorrect: !o.isCorrect } : o))
        : q.options.map((o) => ({ ...o, isCorrect: o.key === optionKey }));
    updateQuestion(q.key, { options });
  };

  const move = (index: number, delta: number) =>
    setQuestions((list) => {
      const next = [...list];
      const [item] = next.splice(index, 1);
      next.splice(index + delta, 0, item);
      return next;
    });

  const payload = (): SaveQuizPayload => ({
    title: title.trim(),
    description: description.trim() || undefined,
    questions: questions.map((q) => ({
      prompt: q.prompt.trim(),
      type: q.type,
      weight: q.weight,
      explanation: q.explanation.trim() || undefined,
      options: q.options.map((o) => ({ label: o.label.trim(), isCorrect: o.isCorrect })),
    })),
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Dê um título ao quiz.");
    try {
      setSaving(true);
      if (id) await updateQuizApi(id, payload());
      else await createQuizApi(payload());
      toast.success("Quiz salvo.");
      navigate(QUIZ_HUB_PAGE(), { replace: true });
    } catch (err: any) {
      toast.error(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!id) return;
    try {
      setSaving(true);
      const copy = await duplicateQuizApi(id);
      toast.success("Quiz duplicado. Agora você pode editar a cópia.");
      navigate(QUIZ_EDIT_PAGE(copy.id), { replace: true });
    } catch (err: any) {
      toast.error(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const readOnly = locked;

  return (
    <div className="quizPage">
      <main className="quizPage__main quizPage__main--narrow">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Quiz", onClick: () => navigate(QUIZ_HUB_PAGE()) },
            { label: id ? "Editar quiz" : "Novo quiz" },
          ]}
          title={id ? (readOnly ? "Quiz já respondido" : "Editar quiz") : "Novo quiz"}
          subtitle="Marque a resposta certa de cada pergunta. Na múltipla escolha, o supervisor só pontua se marcar todas as certas e nenhuma errada."
          onBack={goBack}
          backLabel="Voltar para Quiz"
        />

        {loading ? (
          <p className="quizPage__loading">Carregando...</p>
        ) : (
          <form className="quizEditor" onSubmit={handleSave}>
            {readOnly ? (
              <div className="quizNotice" role="status">
                <EcoIcon name="lock" size={18} />
                <span>
                  Este quiz já foi respondido e não pode mudar, para não alterar notas já dadas. Duplique para fazer uma nova
                  versão.
                </span>
                <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--primary" onClick={handleDuplicate} disabled={saving}>
                  <EcoIcon name="copy" size={16} />
                  Duplicar
                </button>
              </div>
            ) : null}

            <section className="ecoCard quizPanel">
              <div className="ecoField">
                <label htmlFor="quiz-title">Título</label>
                <input
                  id="quiz-title"
                  className="ecoInput"
                  maxLength={160}
                  value={title}
                  disabled={readOnly}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex.: Segurança na loja"
                />
              </div>
              <div className="ecoField">
                <label htmlFor="quiz-desc">
                  Descrição <span className="ecoLabel__optional">(opcional, aparece na capa para o supervisor)</span>
                </label>
                <textarea
                  id="quiz-desc"
                  className="ecoInput quizTextarea"
                  maxLength={2000}
                  rows={3}
                  value={description}
                  disabled={readOnly}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </section>

            {questions.map((q, index) => (
              <section key={q.key} className="ecoCard quizPanel quizQuestion" aria-label={`Pergunta ${index + 1}`}>
                <div className="quizQuestion__head">
                  <span className="quizQuestion__number">Pergunta {index + 1}</span>
                  {!readOnly ? (
                    <div className="quizActions">
                      <button
                        type="button"
                        className="ecoBtn ecoBtn--sm ecoBtn--icon"
                        onClick={() => move(index, -1)}
                        disabled={index === 0}
                        aria-label="Subir pergunta"
                      >
                        <EcoIcon name="arrowUp" size={16} />
                      </button>
                      <button
                        type="button"
                        className="ecoBtn ecoBtn--sm ecoBtn--icon"
                        onClick={() => move(index, 1)}
                        disabled={index === questions.length - 1}
                        aria-label="Descer pergunta"
                      >
                        <EcoIcon name="arrowDown" size={16} />
                      </button>
                      <button
                        type="button"
                        className="ecoBtn ecoBtn--sm ecoBtn--icon ecoBtn--danger"
                        onClick={() => setQuestions((list) => list.filter((x) => x.key !== q.key))}
                        disabled={questions.length === 1}
                        aria-label="Excluir pergunta"
                      >
                        <EcoIcon name="x" size={16} />
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="ecoField">
                  <label htmlFor={`p-${q.key}`}>Enunciado</label>
                  <textarea
                    id={`p-${q.key}`}
                    className="ecoInput quizTextarea"
                    rows={2}
                    maxLength={1000}
                    value={q.prompt}
                    disabled={readOnly}
                    onChange={(e) => updateQuestion(q.key, { prompt: e.target.value })}
                  />
                </div>

                <div className="quizQuestion__row">
                  <div className="ecoField">
                    <label htmlFor={`t-${q.key}`}>Tipo</label>
                    <select
                      id={`t-${q.key}`}
                      className="ecoSelect"
                      value={q.type}
                      disabled={readOnly}
                      onChange={(e) => changeType(q, e.target.value as QuizQuestionType)}
                    >
                      {(Object.keys(QUESTION_TYPE_LABEL) as QuizQuestionType[]).map((t) => (
                        <option key={t} value={t}>
                          {QUESTION_TYPE_LABEL[t]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ecoField quizQuestion__weight">
                    <label htmlFor={`w-${q.key}`}>Peso</label>
                    <input
                      id={`w-${q.key}`}
                      className="ecoInput"
                      type="number"
                      min={1}
                      max={10}
                      value={q.weight}
                      disabled={readOnly}
                      onChange={(e) => updateQuestion(q.key, { weight: Math.min(10, Math.max(1, Number(e.target.value) || 1)) })}
                    />
                  </div>
                </div>

                <fieldset className="quizOptions">
                  <legend className="ecoLabel">
                    Opções — marque {q.type === "MULTI_CHOICE" ? "as corretas" : "a correta"}
                  </legend>
                  {q.options.map((o, oi) => (
                    <div key={o.key} className={`quizOption${o.isCorrect ? " is-correct" : ""}`}>
                      <input
                        type={q.type === "MULTI_CHOICE" ? "checkbox" : "radio"}
                        name={`c-${q.key}`}
                        checked={o.isCorrect}
                        disabled={readOnly}
                        onChange={() => toggleCorrect(q, o.key)}
                        aria-label={`Opção ${oi + 1} é correta`}
                      />
                      <input
                        className="ecoInput"
                        maxLength={500}
                        value={o.label}
                        disabled={readOnly || q.type === "TRUE_FALSE"}
                        placeholder={`Opção ${oi + 1}`}
                        aria-label={`Texto da opção ${oi + 1}`}
                        onChange={(e) =>
                          updateQuestion(q.key, {
                            options: q.options.map((x) => (x.key === o.key ? { ...x, label: e.target.value } : x)),
                          })
                        }
                      />
                      {!readOnly && q.type !== "TRUE_FALSE" ? (
                        <button
                          type="button"
                          className="ecoBtn ecoBtn--sm ecoBtn--icon"
                          onClick={() => updateQuestion(q.key, { options: q.options.filter((x) => x.key !== o.key) })}
                          disabled={q.options.length <= 2}
                          aria-label={`Excluir opção ${oi + 1}`}
                        >
                          <EcoIcon name="x" size={16} />
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {!readOnly && q.type !== "TRUE_FALSE" && q.options.length < 10 ? (
                    <button
                      type="button"
                      className="ecoLinkBtn quizOptions__add"
                      onClick={() => updateQuestion(q.key, { options: [...q.options, { key: newKey(), label: "", isCorrect: false }] })}
                    >
                      + Adicionar opção
                    </button>
                  ) : null}
                </fieldset>

                <div className="ecoField">
                  <label htmlFor={`e-${q.key}`}>
                    Explicação <span className="ecoLabel__optional">(opcional, aparece no gabarito)</span>
                  </label>
                  <textarea
                    id={`e-${q.key}`}
                    className="ecoInput quizTextarea"
                    rows={2}
                    maxLength={2000}
                    value={q.explanation}
                    disabled={readOnly}
                    onChange={(e) => updateQuestion(q.key, { explanation: e.target.value })}
                  />
                </div>
              </section>
            ))}

            {!readOnly ? (
              <div className="quizEditor__footer">
                <button
                  type="button"
                  className="ecoBtn"
                  onClick={() => setQuestions((list) => [...list, emptyQuestion()])}
                  disabled={questions.length >= 100}
                >
                  <EcoIcon name="plus" size={18} />
                  Adicionar pergunta
                </button>
                <div className="quizActions">
                  <button type="button" className="ecoBtn" onClick={goBack} disabled={saving}>
                    Cancelar
                  </button>
                  <button type="submit" className="ecoBtn ecoBtn--primary" disabled={saving}>
                    {saving ? "Salvando..." : "Salvar quiz"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="quizEditor__footer">
                <span />
                <button
                  type="button"
                  className="ecoBtn ecoBtn--primary"
                  onClick={() => navigate(`${QUIZ_CAMPAIGN_NEW_PAGE()}?quizId=${id}`)}
                >
                  Publicar de novo
                </button>
              </div>
            )}
          </form>
        )}
      </main>
    </div>
  );
};

export default QuizEditorPage;
